import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Modal,
  useTheme,
  Tooltip,
  Button,
  CardContent,
  Stack,
  CardActions,
  Skeleton,
} from "@mui/material";
import { FiArrowUpRight } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import { ImInfo } from "react-icons/im";
import { Link, useLocation, useParams } from "react-router-dom";
import { logEvent } from "services/amplitude.service.js";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import useCategoryData from "../hooks/use-category-data.hook";
import CategoryIcon from "./CategoryIcon";
import { fontSize } from "@mui/system";
import { useSelector } from "react-redux";

function TooltipContent({ description }) {
  if (Array.isArray(description)) {
    return (
      <Stack direction="column">
        {description.map((item, index) => (
          <Typography key={`category-desc-${index}`} variant="body1">
            {item}
          </Typography>
        ))}
      </Stack>
    );
  }
  return <Typography variant="body1">{description}</Typography>;
}

export default function SidebarCard({ category, link, loading }) {
  const [open, setOpen] = useState(false);
  // const [bgIcon, setBgIcon] = useState("");
  const theme = useTheme();
  const isMobile = useIsMobile();
  const {source} = useParams();
  const isMediaBias = source === "rating";  
  const { pathname } = useLocation();
  const isPublishersPage = pathname.includes("/partner-publishers");
  const categoryName = category.categoryName_chr || category.label;
  // const { getCategoryIcon } = useCategoryData(() => {});

  // useEffect(() => {
  //   setBgIcon(getCategoryIcon(category.categoryName_chr));
  // }, [category, getCategoryIcon]);

  const handleInfoClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const modalStyle = {
    display: "flex",
    flexDirection: "column",
    width: "40rem",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 3,
    outline: "none",
    borderRadius: "2rem",
    gap: 1.2,
  };

  const handleClick = () => {
    logEvent("click_category_card", {
      category: categoryName,
    });
  };

  if (loading && !isMediaBias)
    return (
      <Skeleton
        sx={{
          position: "relative",
          minWidth: { xs: "100%", sm: "23vw", md: "17vw" },
          minHeight: { xs: "auto", sm: "18vh", lg: "30vh" },
          backgroundColor: theme.palette.secondary.main,
          borderRadius: 1,
        }}
        variant="rectangular"
        height="25vh"
      />
    );

  return (
    <React.Fragment>
      <Card
        className="tour-cat-section-card"
        elevation={0}
        sx={{
          position: "relative",
          minWidth: { xs: "100%", sm: "23vw", md: "17vw" },
          maxWidth: { xs: "100%", sm: "23vw", md: "17vw" },
          minHeight: { xs: "auto", sm: "18vh", lg: "30vh" },
          backgroundColor: theme.palette.secondary.main,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          wordBreak: "break-word",
        }}
      >
        <CategoryIcon
          iconId={categoryName}
          sx={{
            fill: theme.palette.secondary.dark,
            position: "absolute",
            fontSize: { xs: "20rem", lg: "30rem" },
            right: { xs: "-7rem", lg: "-10rem" },
            bottom: { xs: "-7rem", lg: "-10rem" },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 4.4,
            right: 5.4,
            display: "flex",
            zIndex: 10,
            "& > svg": {
              color: theme.palette.secondary.dark,
            },
            "&:hover > svg": {
              color: theme.palette.primary.dark,
            },
          }}
        >
          <Tooltip
            title={
              !isMobile ? (
                <TooltipContent description={category.description_chr} />
              ) : null
            }
            placement="right"
            arrow
          >
            <Box>
              {!isPublishersPage && 
                <ImInfo
                  onClick={handleInfoClick}
                  size={16}
                  style={{
                    p: 1,
                  }}
                />
              }
            </Box>
          </Tooltip>
        </Box>
        <CardContent>
          <Typography
            component="div"
            sx={{ fontSize: { xs: "2rem", lg: "2.3rem", breakWord: "break" } }}
            key={category}
            color="primary.dark"
            fontFamily={theme.typography.h3.fontFamily}
            fontWeight={theme.typography.h3.fontWeight}
          >
            {categoryName}
          </Typography>
        </CardContent>
        <CardActions onClick={handleClick}>
          <Button
            className="tour-cat-section-card-see-all"
            variant="text"
            size="small"
            to={link}
            component={Link}
            startIcon={<FiArrowUpRight />}
            color="inherit"
          >
            See all
          </Button>
        </CardActions>
      </Card>
      {isMobile && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ImInfo color="secondary.dark" size={15} />
                <Typography>About this category</Typography>
              </Box>
              <AiOutlineClose
                onClick={handleClose}
                size={18}
                style={{
                  cursor: "pointer",
                  color: theme.palette.secondary.dark,
                }}
              />
            </Box>
            <Box sx={{ display: "block" }}>
              <Typography variant="h4" color="primary.dark">
                {categoryName}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: 1 }}>
              <TooltipContent description={category.description_chr} />
            </Box>
          </Box>
        </Modal>
      )}
    </React.Fragment>
  );
}
